import React, { useState, useEffect } from 'react'
import Public from '@mui/icons-material/Public'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import CheckIcon from '@mui/icons-material/Check'
import ReactCountryFlag from "react-country-flag"
import { useAuth } from "./api/authStore"
import RenewProxyConfirmDialog from './RenewProxyConfirmDialog'
import RefundProxyConfirmDialog from './RefundProxyConfirmDialog'
import moment from 'moment'
import useMobile from './hooks/useMobile'
import LoadingSpinner from './components/LoadingSpinner'

const CONN_FORMAT_STORAGE_KEY = 'xsocks_conn_format'
const VALID_CONN_FORMATS = ['uri', 'hostPortUserPass', 'userPassHostPort']

const getSavedConnFormat = () => {
    try {
        const saved = window.localStorage.getItem(CONN_FORMAT_STORAGE_KEY)
        if (saved && VALID_CONN_FORMATS.includes(saved)) return saved
    } catch {
        /* ignore */
    }
    return 'uri'
}

// Компонент тултипа (мобильно-дружественный)
const Tooltip = ({ children, title, onLongPress, isCheckingSpeed }) => {
    const [show, setShow] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [touchTimer, setTouchTimer] = useState(null)
    
    // Определяем, мобильное ли устройство
    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])
    
    const handleTouchStart = (e) => {
        if (isMobile) {
            // Короткое касание - показываем тултип
            setShow(true)
            setTimeout(() => setShow(false), 3000)
            
            // Длительное зажатие - запускаем проверку скорости
            const timer = setTimeout(() => {
                if (onLongPress) {
                    console.log('Long press triggered in ProxyDetailsDialog!') // Отладка
                    onLongPress(e)
                }
            }, 600) // 600ms для длительного нажатия
            
            setTouchTimer(timer)
        }
    }
    
    const handleTouchEnd = (e) => {
        if (isMobile && touchTimer) {
            // Если отпустили до истечения 600ms, отменяем длительное нажатие
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    const handleTouchCancel = (e) => {
        if (isMobile && touchTimer) {
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    return (
        <div 
            className={`shop-confirm-tooltip-wrap${isMobile ? ' shop-confirm-tooltip-wrap--block' : ''}`}
            style={{ 
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitTapHighlightColor: 'transparent'
            }}
            onMouseEnter={() => !isMobile && setShow(true)}
            onMouseLeave={() => !isMobile && setShow(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            {/* Показываем спиннер только если идет проверка скорости */}
            {isCheckingSpeed ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <LoadingSpinner size={22} />
                </div>
            ) : (
                children
            )}
            {show && !isCheckingSpeed && (
                <div className="shop-confirm-tooltip">
                    {title}
                </div>
            )}
        </div>
    )
}

// Компонент для уведомлений
const CustomSnackbar = ({ open, onClose, message, severity = 'success' }) => {
    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => onClose(), 4000)
            return () => clearTimeout(timer)
        }
    }, [open, onClose])

    if (!open) return null

    const icon = severity === 'error' ? '✕' : severity === 'success' ? '✓' : '!'

    return (
        <div className={`shop-toast shop-toast--animated shop-toast--${severity}`}>
            <span className="shop-toast__icon">{icon}</span>
            <span className="shop-toast__message">{message}</span>
            <button type="button" onClick={onClose} className="shop-toast__close">✕</button>
        </div>
    )
}

const ProxyDetailsDialog = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()
    const { isMobile } = useMobile()

    const selectedProxy = props.selectedProxy
    const [saveWhitelistError, setSaveWhitelistError] = useState(false)
    const [whitelist, setWhitelist] = useState(null)
    const [buttonStatus, setButtonDisbled] = useState(true)
    const [isSettingMyIp, setIsSettingMyIp] = useState(false)
    const [connFormat, setConnFormatState] = useState(() => getSavedConnFormat())
    const [connCopied, setConnCopied] = useState(false)

    const setConnFormat = (format) => {
        setConnFormatState(format)
        try {
            window.localStorage.setItem(CONN_FORMAT_STORAGE_KEY, format)
        } catch {
            /* ignore */
        }
    }
    const [openSuccessWhitelistUpdate, setOpenSuccessWhitelistUpdate] = useState(false)
    const [openConfirmRenew, setOpenConfirmRenew] = useState(false)
    const [openConfirmRefund, setOpenConfirmRefund] = useState(false)
    const [renewError, setRenewError] = useState(false)
    const [renewSuccess, setRenewSuccess] = useState(false)
    const [refundError, setRefundError] = useState(false)
    const [refundSuccess, setRefundSuccess] = useState(false)
    const [duplicateTicketError, setDuplicateTicketError] = useState(false)
    
    // Состояние для проверки скорости прокси (как в Proxies.js)
    const [isCheckingSpeed, setIsCheckingSpeed] = useState(false)
    const [isLoadingSpeed, setIsLoadingSpeed] = useState(false)
    const [proxySpeed, setProxySpeed] = useState(null)
    const [speedCheckTime, setSpeedCheckTime] = useState(null)
    
    // CSS стили для анимации спиннера
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // Автоматическое закрытие уведомлений через 4 секунды
    useEffect(() => {
        if (renewSuccess) {
            const timer = setTimeout(() => {
                setRenewSuccess(false)
            }, 4000)
            
            return () => clearTimeout(timer)
        }
    }, [renewSuccess])

    useEffect(() => {
        if (refundSuccess) {
            const timer = setTimeout(() => {
                setRefundSuccess(false)
            }, 4000)
            
            return () => clearTimeout(timer)
        }
    }, [refundSuccess])

    useEffect(() => {
        if (duplicateTicketError) {
            const timer = setTimeout(() => {
                setDuplicateTicketError(false)
            }, 4000)
            
            return () => clearTimeout(timer)
        }
    }, [duplicateTicketError])

    useEffect(() => {
        if (openSuccessWhitelistUpdate) {
            const timer = setTimeout(() => {
                setOpenSuccessWhitelistUpdate(false)
            }, 4000)
            
            return () => clearTimeout(timer)
        }
    }, [openSuccessWhitelistUpdate])

    useEffect(() => {
        if (saveWhitelistError) {
            const timer = setTimeout(() => {
                setSaveWhitelistError(false)
            }, 4000)

            return () => clearTimeout(timer)
        }
    }, [saveWhitelistError])

    useEffect(() => {
        if (renewError) {
            const timer = setTimeout(() => {
                setRenewError(false)
            }, 4000)

            return () => clearTimeout(timer)
        }
    }, [renewError])

    useEffect(() => {
        if (refundError) {
            const timer = setTimeout(() => {
                setRefundError(false)
            }, 4000)

            return () => clearTimeout(timer)
        }
    }, [refundError])

    // Вычисляем время до истечения
    const currentTime = new Date().getTime()
    const endTime = new Date(selectedProxy?.expiresAt).getTime()
    const hoursUntilExpiry = Math.max(0, Math.ceil((endTime - currentTime) / (1000 * 60 * 60)))
    
    // Вычисляем время с момента покупки для refund (60 минут = 1 час)
    const startTime = new Date(selectedProxy?.startedAt).getTime()
    const hoursSincePurchase = Math.floor((currentTime - startTime) / (1000 * 60 * 60))
    const minutesSincePurchase = Math.floor((currentTime - startTime) / (1000 * 60))
    const canRefund = minutesSincePurchase <= 60 // 60 минут после покупки


    // Загрузка данных прокси при открытии диалога
    useEffect(() => {
        const loadProxyData = async () => {
            if (props.open && selectedProxy?.proxyid) {
                try {
                    setIsLoadingSpeed(true)

                    // Загружаем полную информацию о прокси по proxyid
                    const proxyData = await props.apiClient.setToken(getAccessTokenSilently()).getAgentInfo(selectedProxy.proxyid)
                    
                    // Если есть данные о скорости, используем их
                    if (proxyData?.speed?.bytesPerSec != null) {
                        const speed = (proxyData.speed.bytesPerSec / 1000 / 1000).toFixed(2)
                        setProxySpeed(speed)
                        setSpeedCheckTime(proxyData.speed.checkedAt)
                    } else {
                        // Если нет данных о скорости, устанавливаем null
                        setProxySpeed(null)
                    }
                } catch (e) {
                    setProxySpeed(null)
                } finally {
                    setIsLoadingSpeed(false)
                }
            }
        }
        
        loadProxyData()
    }, [props.open, selectedProxy?.proxyid])

    // Инициализация поля whitelist при открытии диалога
    useEffect(() => {
        if (props.open) {
            setWhitelist(selectedProxy?.ipAuth?.whitelist || '')
        }
    }, [props.open, selectedProxy?.rentalid])

    const handleClose = () => {
        props.setOpenDetails(false)
    }

    // Закрытие диалога по нажатию Esc
    useEffect(() => {
        if (!props.open) return

        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                props.setOpenDetails(false)
            }
        }

        window.addEventListener('keydown', handleEsc)
        return () => window.removeEventListener('keydown', handleEsc)
    }, [props.open])

    const handleSetSaveErrorClose = () => {
        setSaveWhitelistError(false)
    }

    const handleSetOpenSuccessWhitelistUpdate = () => {
        setOpenSuccessWhitelistUpdate(false)
    }

    const handleSetRenewErrorClose = () => {
        setRenewError(false)
    }

    const handleSetRenewSuccessClose = () => {
        setRenewSuccess(false)
    }

    const handleSetRefundSuccessClose = () => {
        setRefundSuccess(false)
    }

    const handleSetRefundErrorClose = () => {
        setRefundError(false)
    }

    const handleSetDuplicateTicketErrorClose = () => {
        setDuplicateTicketError(false)
    }

    // Функция проверки скорости прокси
    const checkSpeed = async () => {
        try {
            setIsCheckingSpeed(true)

            const response = await props.apiClient.setToken(getAccessTokenSilently()).checkProxySpeed(selectedProxy?.proxyid)
            const speed = (response?.bytesPerSec != null) ? (response.bytesPerSec / 1000 / 1000).toFixed(2) : null
            
            setProxySpeed(speed)
            setSpeedCheckTime(response?.checkedAt)
        } catch (e) {
            // В случае ошибки показываем "Error"
            setProxySpeed('Error')
        } finally {
            setIsCheckingSpeed(false)
        }
    }

    const handleWhiteListChange = (e) => {
        const regexExpIP = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/gi

        if (e.target.value == "") {
            setButtonDisbled(true)
            setWhitelist(e.target.value)
            return
        }

        const ipsArr = e.target.value.split(',')
        let checkedCount = 0

        ipsArr.forEach(element => {
            if (!element.match(regexExpIP)) {
                setButtonDisbled(true)
            } else {
                checkedCount++
            }
        })

        if (checkedCount == ipsArr.length) {
            setButtonDisbled(false)
        }

        setWhitelist(e.target.value)
    }

    const handleWhitelistSave = async () => {
        try {
            const response = await props.apiClient.setToken(getAccessTokenSilently()).saveProxyWhitelist(selectedProxy.rentalid, whitelist)

            setOpenSuccessWhitelistUpdate(true)
            props.setReloadActive(true)
        } catch (e) {
            setSaveWhitelistError(true)
        }
    }

    // Запрос на запись текущего IP клиента в whitelist (IP определяет бэкенд)
    const handleUseMyIp = async () => {
        try {
            setIsSettingMyIp(true)

            const response = await props.apiClient.setToken(getAccessTokenSilently()).saveProxyWhitelist(selectedProxy.rentalid, 'userIP')

            // Берём IP, который вернул сервер
            const newWhitelist = response?.ipAuth?.whitelist || ''
            setWhitelist(newWhitelist)
            setButtonDisbled(true)
            setOpenSuccessWhitelistUpdate(true)
            props.setReloadActive(true)
        } catch (e) {
            setSaveWhitelistError(true)
        } finally {
            setIsSettingMyIp(false)
        }
    }

    const handleRenewClick = async () => {
        try {
            setOpenConfirmRenew(true)
            props.setOpenDetails(false) // Закрываем основной диалог
        } catch (e) {
        }
    }

    const handleRefundClick = async () => {
        try {
            setOpenConfirmRefund(true)
            props.setOpenDetails(false) // Закрываем основной диалог
        } catch (e) {
        }
    }

    const renewProxy = async () => {
        try {
            const response = await props.apiClient.setToken(getAccessTokenSilently()).renewProxy(selectedProxy.rentalid)

            setRenewSuccess(true)
            props.refreshUserData?.()
            props.setReloadActive(true)
            props.setOpenDetails(false)
        } catch (e) {
            setRenewError(true)
        }
    }

    const refundProxy = async (reason) => {
        try {
            const response = await props.apiClient.setToken(getAccessTokenSilently()).createRefundTicket(selectedProxy.rentalid, reason)

            setOpenConfirmRefund(false)
            setRefundSuccess(true)
            props.setOpenDetails(false)
        } catch (e) {
            setOpenConfirmRefund(false)
            
            // Проверяем, является ли это ошибкой дублирующего тикета
            if (e.response && e.response.status === 401) {
                // Это дублирующий тикет - показываем специальное сообщение
                setDuplicateTicketError(true)
            } else {
                setRefundError(true)
            }
        }
    }

    // Получаем полный IP адрес из proxyinfo
    const getFullIPAddress = () => {
        return selectedProxy?.proxyinfo?.ip || 'N/A'
    }

    // Доступные форматы строки подключения
    const connFormats = [
        { id: 'uri', label: 'URI' },
        { id: 'hostPortUserPass', label: 'host:port:user:pass' },
        { id: 'userPassHostPort', label: 'user:pass@host:port' },
    ]

    // Сборка строки подключения в выбранном формате
    const buildConnectionString = () => {
        const ip = selectedProxy?.socksAuth?.ip || ''
        const port = selectedProxy?.socksAuth?.port || ''
        const user = selectedProxy?.socksAuth?.user || ''
        const pass = selectedProxy?.socksAuth?.password || ''

        switch (connFormat) {
            case 'hostPortUserPass':
                return `${ip}:${port}:${user}:${pass}`
            case 'userPassHostPort':
                return `${user}:${pass}@${ip}:${port}`
            case 'uri':
            default:
                return `socks5h://${user}:${pass}@${ip}:${port}`
        }
    }

    const handleCopyConnectionString = async () => {
        try {
            await navigator.clipboard.writeText(buildConnectionString())
            setConnCopied(true)
            setTimeout(() => setConnCopied(false), 1500)
        } catch (e) {
            /* ignore */
        }
    }


    return (
        <>
            {/* Модальное окно */}
            {props.open && (
                <div className="shop-confirm-overlay" onClick={handleClose}>
                    <div className="shop-confirm-modal shop-confirm-modal--scroll shop-confirm-modal--sidebar" onClick={(e) => e.stopPropagation()}>
                        
                        {isMobile ? (
                            <button
                                type="button"
                                onClick={handleClose}
                                className="shop-confirm-btn-secondary shop-confirm-btn-secondary--full shop-confirm-close-mobile"
                                aria-label="Close"
                            >
                                Close
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleClose}
                                className="shop-confirm-close"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        )}

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-xl)',
                            paddingTop: isMobile ? 0 : 'var(--spacing-lg)'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '30px',
                                borderRadius: 'var(--radius-sm)',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <ReactCountryFlag 
                                    countryCode={selectedProxy?.proxyinfo?.geo?.cc} 
                                    svg 
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                            <div>
                                <h2 className="shop-confirm-panel__heading" style={{ fontSize: 'var(--text-xl)' }}>
                                    <span className="shop-confirm-accent" style={{ textTransform: 'uppercase' }}>Proxy ID:</span>{' '}
                                    {selectedProxy?.rentalid}
                                </h2>
                                <p className="shop-confirm-panel__sub">
                                    {selectedProxy?.proxyinfo?.geo?.reg} • {selectedProxy?.proxyinfo?.geo?.city} • {selectedProxy?.proxyinfo?.geo?.zip} • {selectedProxy?.proxyinfo?.geo?.isp}
                                </p>
                            </div>
                        </div>

                        <div className="shop-confirm-panel">
                            <div style={{
                                display: 'flex',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: isMobile ? 'var(--spacing-xs)' : 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-sm)'
                            }}>
                                <h3 style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: 'var(--text-lg)',
                                    fontWeight: '600',
                                    margin: '0',
                                    whiteSpace: 'nowrap',
                                    color: 'var(--text-primary)'
                                }}>
                                    <Public className="shop-confirm-public-icon" sx={{ fontSize: 22 }} />
                                    <span className="shop-confirm-accent" style={{ textTransform: 'uppercase' }}>Proxy IP</span>
                                </h3>
                                <span className="shop-confirm-mono" style={{ fontSize: 'var(--text-base)' }}>
                                    {getFullIPAddress()}
                                </span>
                            </div>
                            <p className="shop-confirm-panel__sub">
                                This proxy will be rented by you for 24 hours from the time of purchase.
                            </p>
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                            <h3 className="shop-confirm-section-title">
                                Authorization by Username/Password
                            </h3>
                            
                            <div style={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p className="shop-confirm-field-label">
                                        Socks5 Server:Port
                                    </p>
                                    <p className="shop-confirm-field-value">
                                        {selectedProxy?.socksAuth?.ip}:{selectedProxy?.socksAuth?.port}
                                    </p>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p className="shop-confirm-field-label">
                                        Username:Password
                                    </p>
                                    <p className="shop-confirm-field-value">
                                        {selectedProxy?.socksAuth?.user}:{selectedProxy?.socksAuth?.password}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                            <h3 className="shop-confirm-section-title">
                                Authorization by IP
                            </h3>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                <div>
                                    <p className="shop-confirm-field-label">
                                        Socks5 Server:Port
                                    </p>
                                    <p className="shop-confirm-field-value">
                                        {selectedProxy?.socksAuth?.ip}:{selectedProxy?.ipAuth?.port}
                                    </p>
                                </div>
                                <div>
                                    <p className="shop-confirm-section-title shop-confirm-section-title--inline">
                                        Whitelist{' '}
                                        <span className="shop-confirm-title-case-normal">(e.g. 1.1.1.1,1.1.1.2)</span>
                                    </p>
                                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                                        <button
                                            type="button"
                                            className="btn btn-outline btn-small"
                                            disabled={isSettingMyIp}
                                            onClick={handleUseMyIp}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: '70px',
                                                flexShrink: 0,
                                                opacity: isSettingMyIp ? 0.7 : 1,
                                                cursor: isSettingMyIp ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            {isSettingMyIp ? <LoadingSpinner size={20} /> : 'My IP'}
                                        </button>
                                        <input
                                            type="text"
                                            className="shop-confirm-input"
                                            value={whitelist ?? ''}
                                            onChange={handleWhiteListChange}
                                            placeholder="Enter IP addresses"
                                        />
                                        <button
                                            className="btn btn-outline btn-small"
                                            disabled={buttonStatus}
                                            onClick={handleWhitelistSave}
                                            style={{ minWidth: '60px', flexShrink: 0 }}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="shop-confirm-panel" style={{ marginBottom: 'var(--spacing-lg)' }}>
                            <h3 className="shop-confirm-section-title" style={{ marginBottom: 'var(--spacing-sm)' }}>
                                Connection String
                            </h3>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 'var(--spacing-xs)',
                                marginBottom: 'var(--spacing-sm)'
                            }}>
                                {connFormats.map((fmt) => (
                                    <button
                                        key={fmt.id}
                                        type="button"
                                        onClick={() => setConnFormat(fmt.id)}
                                        className={`shop-confirm-chip${connFormat === fmt.id ? ' shop-confirm-chip--active' : ''}`}
                                    >
                                        {fmt.label}
                                    </button>
                                ))}
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-sm)'
                            }}>
                                <button
                                    type="button"
                                    onClick={handleCopyConnectionString}
                                    title={connCopied ? 'Copied!' : 'Copy'}
                                    className={`shop-confirm-copy-btn${connCopied ? ' shop-confirm-copy-btn--copied' : ''}`}
                                >
                                    {connCopied
                                        ? <CheckIcon style={{ fontSize: '14px' }} />
                                        : <ContentCopyOutlinedIcon style={{ fontSize: '14px' }} />}
                                </button>
                                <p className="shop-confirm-conn-text">
                                    {buildConnectionString()}
                                </p>
                            </div>
                        </div>



                        {/* Кнопки действий */}
                        <div className="shop-confirm-actions" style={{
                            display: 'flex',
                            gap: 'var(--spacing-md)',
                            justifyContent: 'center',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>
                            {!isMobile && (
                                (isCheckingSpeed || isLoadingSpeed) ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline"
                                        style={{ 
                                            minWidth: '120px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            opacity: 0.7,
                                            cursor: 'not-allowed'
                                        }}
                                        disabled
                                    >
                                        {isCheckingSpeed ? (
                                            <LoadingSpinner size={22} />
                                        ) : (
                                            <LoadingSpinner />
                                        )}
                                    </button>
                                ) : (
                                    <Tooltip 
                                        title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Speed test result'}
                                    >
                                        <button
                                            type="button"
                                            onClick={checkSpeed}
                                            className="btn btn-outline"
                                            style={{ 
                                                minWidth: '120px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            {proxySpeed === 'Error' ? 'Error' : proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'}
                                        </button>
                                    </Tooltip>
                                )
                            )}
                            
                            {isMobile && (
                                <Tooltip 
                                    title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Hold to test speed'}
                                    onLongPress={() => {
                                        if (!isCheckingSpeed && !isLoadingSpeed) {
                                            checkSpeed()
                                        }
                                    }}
                                    isCheckingSpeed={isCheckingSpeed}
                                >
                                    <button
                                        type="button"
                                        className="btn btn-outline"
                                        onClick={checkSpeed}
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            opacity: (isCheckingSpeed || isLoadingSpeed) ? 0.7 : 1,
                                            cursor: (isCheckingSpeed || isLoadingSpeed) ? 'not-allowed' : 'pointer'
                                        }}
                                        disabled={isCheckingSpeed || isLoadingSpeed}
                                    >
                                        {isLoadingSpeed ? (
                                            <LoadingSpinner />
                                        ) : isCheckingSpeed ? (
                                            <LoadingSpinner size={22} />
                                        ) : (
                                            proxySpeed === 'Error' ? 'Error' : proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'
                                        )}
                                    </button>
                                </Tooltip>
                            )}
                            <button
                                className={`btn ${canRefund ? 'btn-buy' : 'btn-outline'} btn-small`}
                                onClick={handleRefundClick}
                                disabled={!canRefund}
                                style={{ 
                                    width: isMobile ? '100%' : undefined,
                                    minWidth: isMobile ? undefined : '100px',
                                    height: isMobile ? '40px' : undefined,
                                    opacity: canRefund ? 1 : 0.5
                                }}
                            >
                                Refund
                            </button>
                            <button
                                className="btn btn-buy btn-small"
                                onClick={handleRenewClick}
                                style={{
                                    width: isMobile ? '100%' : undefined,
                                    minWidth: isMobile ? undefined : '100px',
                                    height: isMobile ? '40px' : undefined
                                }}
                            >
                                Renew
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Диалоги подтверждения */}
            <RenewProxyConfirmDialog 
                openConfirmRenew={openConfirmRenew} 
                setOpenConfirmRenew={setOpenConfirmRenew}
                renewProxy={renewProxy}
                selectedProxy={selectedProxy}
            />

            <RefundProxyConfirmDialog 
                openConfirmRefund={openConfirmRefund}
                refundProxy={refundProxy}
                setOpenConfirmRefund={setOpenConfirmRefund}
                selectedProxy={selectedProxy}
            />

            {/* Уведомления */}
            <CustomSnackbar
                open={saveWhitelistError}
                onClose={handleSetSaveErrorClose}
                message="Error saving whitelist."
                severity="error"
            />

            <CustomSnackbar
                open={openSuccessWhitelistUpdate}
                onClose={handleSetOpenSuccessWhitelistUpdate}
                message="Proxy whitelist updated!"
                severity="success"
            />

            <CustomSnackbar
                open={renewError}
                onClose={handleSetRenewErrorClose}
                message="Error renew proxy."
                severity="error"
            />

            <CustomSnackbar
                open={renewSuccess}
                onClose={handleSetRenewSuccessClose}
                message="Proxy renewed successfully!"
                severity="success"
            />

            <CustomSnackbar
                open={refundError}
                onClose={handleSetRefundErrorClose}
                message="Error creating refund ticket."
                severity="error"
            />

            <CustomSnackbar
                open={refundSuccess}
                onClose={handleSetRefundSuccessClose}
                message="Refund ticket created successfully!"
                severity="success"
            />

            <CustomSnackbar
                open={duplicateTicketError}
                onClose={handleSetDuplicateTicketErrorClose}
                message="A refund ticket for this proxy already exists!"
                severity="error"
            />
        </>
    )
}

export default ProxyDetailsDialog